import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import WorkCard from "@/components/feature/WorkCard";
import { works } from "@/data/catalog";
import { collectYears, searchWorks } from "@/utils/workUtils";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

type TypeFilter = "all" | "movie" | "drama";

const TYPE_OPTIONS: { label: string; value: TypeFilter }[] = [
  { label: "全部", value: "all" },
  { label: "伦理电影", value: "movie" },
  { label: "伦理电视剧", value: "drama" },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";
  const [input, setInput] = useState(q);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [yearFilter, setYearFilter] = useState<string>("all");

  useDocumentMeta({
    title: q ? `搜索“${q}” - 韩国伦理影视` : "搜索 - 韩国伦理影视",
    description: q
      ? `在韩国伦理平台搜索“${q}”相关的韩国伦理电影与电视剧，查看匹配作品的剧情介绍与影评。`
      : "在韩国伦理平台搜索韩国伦理电影与电视剧，支持标题、剧情简介与题材标签匹配。",
    keywords: `韩国伦理搜索,${q ? `${q},` : ""}韩国伦理电影,韩国伦理电视剧`,
  });

  const yearOptions = useMemo(() => collectYears(works), []);

  const hotWorks = useMemo(
    () => [...works].sort((a, b) => b.views - a.views).slice(0, 5),
    [],
  );

  const results = useMemo(() => {
    let list = searchWorks(works, q);
    if (typeFilter !== "all") list = list.filter((w) => w.type === typeFilter);
    if (yearFilter !== "all") list = list.filter((w) => String(w.year) === yearFilter);
    return list;
  }, [q, typeFilter, yearFilter]);

  const runSearch = (event: FormEvent) => {
    event.preventDefault();
    const keyword = input.trim();
    setTypeFilter("all");
    setYearFilter("all");
    if (keyword) {
      setSearchParams({ q: keyword });
    } else {
      setSearchParams({});
    }
  };

  const hasQuery = q.trim().length > 0;

  return (
    <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />

      <header className="mt-5 mb-6">
        <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950">搜索作品</h1>
        <p className="mt-3 text-sm md:text-base text-foreground-600">
          输入关键词，支持匹配作品标题、剧情简介、题材标签与导演信息。
        </p>

        <form
          onSubmit={runSearch}
          className="mt-5 flex flex-col sm:flex-row gap-3 max-w-2xl"
        >
          <div className="flex items-center flex-1 bg-background-100 border border-background-200 rounded-md h-11 pl-3 pr-3 focus-within:border-primary-400 transition-colors">
            <i className="ri-search-line text-foreground-400" />
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              type="search"
              name="keyword"
              placeholder="例如：家庭伦理、心理伦理、静默的证词"
              className="bg-transparent outline-none text-sm px-2 flex-1 text-foreground-800 placeholder:text-foreground-400"
            />
          </div>
          <button
            type="submit"
            className="h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            搜索
          </button>
        </form>
      </header>

      {hasQuery && (
        <>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5 pb-5 border-b border-background-200">
            <p className="text-sm text-foreground-600">
              {results.length > 0 ? (
                <>
                  找到与“<span className="text-foreground-950 font-medium">{q}</span>”相关的作品
                  <span className="text-foreground-950 font-semibold"> {results.length} </span>个
                </>
              ) : (
                <>关键词“<span className="text-foreground-950 font-medium">{q}</span>”暂无匹配结果</>
              )}
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
                {TYPE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setTypeFilter(option.value)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      typeFilter === option.value
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-600 hover:text-primary-700"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <select
                value={yearFilter}
                onChange={(e) => setYearFilter(e.target.value)}
                className="h-9 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-700 cursor-pointer outline-none focus:border-primary-400"
                aria-label="按年份筛选"
              >
                <option value="all">全部年份</option>
                {yearOptions.map((year) => (
                  <option key={year} value={String(year)}>
                    {year} 年
                  </option>
                ))}
              </select>
            </div>
          </div>

          {results.length === 0 ? (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-100/50 py-16 flex flex-col items-center gap-3 text-center">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
                <i className="ri-emotion-sad-line text-3xl" />
              </span>
              <p className="font-medium text-foreground-800">未找到符合“{q}”的作品</p>
              <p className="text-sm text-foreground-500">
                换个关键词试试，或从下面的热门作品开始浏览。
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 md:gap-5">
              {results.map((work) => (
                <WorkCard key={work.id} work={work} />
              ))}
            </div>
          )}
        </>
      )}

      <section className={hasQuery ? "mt-12" : "mt-2"}>
        <div className="flex items-center gap-3 mb-5">
          <span className="w-1 h-6 rounded-full bg-accent-500" />
          <h2 className="font-heading font-bold text-xl text-foreground-950">
            {hasQuery ? "热门作品推荐" : "大家都在看"}
          </h2>
        </div>
        {!hasQuery && (
          <p className="text-sm text-foreground-500 mb-5">
            还没有输入关键词？先看看平台上当前最受关注的伦理影视作品。
          </p>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 md:gap-5">
          {hotWorks.map((work) => (
            <WorkCard key={work.id} work={work} />
          ))}
        </div>
      </section>

      <div className="mt-12 rounded-lg border border-background-200 bg-background-100/50 p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-foreground-600">想按题材系统地浏览？试试分类页的多维筛选。</p>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/category/erotic"
            className="px-4 h-10 inline-flex items-center rounded-md border border-background-300 text-foreground-700 text-sm font-medium hover:border-primary-300 transition-colors"
          >
            伦理电影
          </Link>
          <Link
            to="/category/drama"
            className="px-4 h-10 inline-flex items-center rounded-md border border-background-300 text-foreground-700 text-sm font-medium hover:border-primary-300 transition-colors"
          >
            伦理电视剧
          </Link>
        </div>
      </div>
    </div>
  );
}